/*
 * Cart drawer upsell products
 *
 * The products themselves are rendered by Liquid (cart-line-items, part
 * 'upsell-items') and refreshed by the theme's <cart-items> after every
 * Ajax cart update. This element:
 *  - re-initialises the theme's <grid-slider> (desktop mouse drag) once the
 *    drawer is visible and after each refresh,
 *  - starts the row at the first card,
 *  - drives the prev / next arrows on every device (the theme slider only adds
 *    arrows on non-touch desktops) and the pagination dots.
 * Touch swiping is native scrolling; the row is registered with the theme's
 * scroll lock via data-scroll-lock-scrollable in the markup.
 */
(function () {
  'use strict';

  const selectors = {
    slider: 'grid-slider',
    list: '[data-grid-slider]',
    card: '[data-grid-item]',
    arrows: '[data-cart-upsell-arrows]',
    prev: '[data-cart-upsell-prev]',
    next: '[data-cart-upsell-next]',
    dots: '[data-cart-upsell-dots]',
  };

  class CartUpsellProducts extends HTMLElement {
    constructor() {
      super();
      this.reinit = this.reinit.bind(this);
      this.onToggle = this.onToggle.bind(this);
      this.syncArrows = this.syncArrows.bind(this);
      this.onArrowClick = this.onArrowClick.bind(this);
      this.onDotClick = this.onDotClick.bind(this);
    }

    connectedCallback() {
      this.widget = this.closest('details');
      this.list = this.querySelector(selectors.list);
      this.arrows = this.querySelector(selectors.arrows);
      this.dots = this.querySelector(selectors.dots);

      document.addEventListener('theme:cart-drawer:open', this.reinit);
      document.addEventListener('theme:cart:change', this.reinit);
      document.addEventListener('theme:resize:width', this.syncArrows);
      this.widget?.addEventListener('toggle', this.onToggle);
      this.list?.addEventListener('scroll', this.syncArrows, {passive: true});
      this.arrows?.addEventListener('click', this.onArrowClick);
      this.dots?.addEventListener('click', this.onDotClick);

      this.reinit();
    }

    disconnectedCallback() {
      document.removeEventListener('theme:cart-drawer:open', this.reinit);
      document.removeEventListener('theme:cart:change', this.reinit);
      document.removeEventListener('theme:resize:width', this.syncArrows);
      this.widget?.removeEventListener('toggle', this.onToggle);
      this.list?.removeEventListener('scroll', this.syncArrows);
      this.arrows?.removeEventListener('click', this.onArrowClick);
      this.dots?.removeEventListener('click', this.onDotClick);
      clearTimeout(this.timer);
    }

    onToggle() {
      if (this.widget?.open) this.reinit();
    }

    /**
     * After every cart refresh the theme swaps the cards inside the same scrolling
     * element, so the browser would keep the old scroll position. Start from the
     * first card, then re-initialise the theme slider once the drawer / accordion
     * animation has finished so it measures the real width.
     */
    reinit() {
      clearTimeout(this.timer);
      this.resetPosition();
      this.buildDots();
      this.syncArrows();

      this.timer = setTimeout(() => {
        const slider = this.querySelector(selectors.slider);
        if (slider && typeof slider.destroy === 'function') {
          slider.destroy();
          slider.dispatchEvent(new CustomEvent('theme:grid-slider:init', {bubbles: false}));
        }

        this.resetPosition();
        this.buildDots();
        requestAnimationFrame(this.syncArrows);
      }, 350);
    }

    resetPosition() {
      if (this.list && this.list.scrollLeft !== 0) this.list.scrollLeft = 0;
    }

    getOffsets() {
      const cards = this.list ? Array.from(this.list.querySelectorAll(selectors.card)) : [];
      return cards.map((card) => card.offsetLeft - cards[0].offsetLeft);
    }

    // One dot per card (every card can reach the left edge); rebuilt when the cards change
    buildDots() {
      if (!this.dots || !this.list) return;

      const count = this.list.querySelectorAll(selectors.card).length;
      if (this.dots.childElementCount === count) return;

      const fragment = document.createDocumentFragment();
      for (let index = 0; index < count; index++) {
        const dot = document.createElement('button');
        dot.type = 'button';
        dot.className = 'flickity-page-dot';
        dot.setAttribute('data-index', index);
        dot.setAttribute('aria-label', `${index + 1} / ${count}`);
        fragment.appendChild(dot);
      }
      this.dots.replaceChildren(fragment);
    }

    // Arrows and dots show only when the row scrolls; Previous is off at the first card, Next at the last
    syncArrows() {
      if (!this.list) return;

      const max = this.list.scrollWidth - this.list.clientWidth;
      const scrollable = max > 1;

      if (this.arrows) {
        const prev = this.arrows.querySelector(selectors.prev);
        const next = this.arrows.querySelector(selectors.next);

        this.arrows.hidden = !scrollable;
        if (prev) prev.disabled = this.list.scrollLeft <= 1;
        if (next) next.disabled = this.list.scrollLeft >= max - 1;
      }

      if (this.dots) {
        this.dots.hidden = !scrollable;

        // Active dot: the card closest to the left edge
        const offsets = this.getOffsets();
        let active = 0;
        offsets.forEach((offset, index) => {
          if (Math.abs(offset - this.list.scrollLeft) < Math.abs(offsets[active] - this.list.scrollLeft)) active = index;
        });

        Array.from(this.dots.children).forEach((dot, index) => {
          const selected = index === active;
          dot.classList.toggle('is-selected', selected);
          if (selected) {
            dot.setAttribute('aria-current', 'true');
          } else {
            dot.removeAttribute('aria-current');
          }
        });
      }
    }

    onDotClick(event) {
      const dot = event.target.closest('[data-index]');
      if (!dot || !this.list) return;

      const target = this.getOffsets()[parseInt(dot.getAttribute('data-index'), 10)];
      if (target !== undefined) this.list.scrollTo({left: target, behavior: 'smooth'});
    }

    // Move to the previous / next card's left edge (the same position as the first card)
    onArrowClick(event) {
      const button = event.target.closest(`${selectors.prev}, ${selectors.next}`);
      if (!button || !this.list) return;

      event.preventDefault();

      const offsets = this.getOffsets();
      if (!offsets.length) return;

      const current = this.list.scrollLeft;
      const forward = button.matches(selectors.next);
      const target = forward ? offsets.find((offset) => offset > current + 1) : [...offsets].reverse().find((offset) => offset < current - 1);

      if (target === undefined) return;

      this.list.scrollTo({left: target, behavior: 'smooth'});
    }
  }

  if (!customElements.get('cart-upsell-products')) {
    customElements.define('cart-upsell-products', CartUpsellProducts);
  }
})();
